import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { emailToDisplayName } from '../ActionPlan/utils/formOptions';
import { AdminColors } from '../Admin/constants/adminColors';
import AdminModal from '../Admin/components/AdminModal';
import AdminButton from '../Admin/components/AdminButton';
import FontFamily from '../../constants/fonts';
import { formatLongDate } from './formatDate';
import {
  fetchCoachingHistory,
  CoachingHistoryFilterOptions,
  CoachingHistoryParams,
  CoachingHistoryRecord,
} from './peopleHubApi';
import AuditHistory from '../ActionPlan/components/AuditHistory';

// ─── Column / filter config ───────────────────────────────────────────────────
type FilterParamKey = 'employeeNames' | 'actionTypes' | 'focusAreas' | 'managers' | 'sources' | 'statuses' | 'results';

// `width` is both the minimum and the proportional-grow basis, so the table is at least
// TABLE_MIN_WIDTH wide (scrolls horizontally) and stretches to fill wider containers.
interface ColDef {
  key: keyof CoachingHistoryRecord;
  label: string;
  width: number;
  sortKey: string;
  filterKey?: FilterParamKey;
}

const ALL_COLUMNS: ColDef[] = [
  { key: 'employeeName',     label: 'Employee Name',     width: 200, sortKey: 'employeeName',     filterKey: 'employeeNames' },
  { key: 'actionType',       label: 'Action Type',       width: 130, sortKey: 'actionType',       filterKey: 'actionTypes' },
  { key: 'focusArea',        label: 'Focus Area',        width: 160, sortKey: 'focusArea',        filterKey: 'focusAreas' },
  { key: 'actionPlan',       label: 'Action Plan',       width: 240, sortKey: 'actionPlan' },
  { key: 'managerSubmitted', label: 'Manager Submitted', width: 180, sortKey: 'managerSubmitted', filterKey: 'managers' },
  { key: 'activityDate',     label: 'Activity Date',     width: 140, sortKey: 'activityDate' },
  { key: 'source',           label: 'Source',            width: 130, sortKey: 'source',           filterKey: 'sources' },
  { key: 'status',           label: 'Status',            width: 156, sortKey: 'status',           filterKey: 'statuses' },
  { key: 'result',           label: 'Result',            width: 210, sortKey: 'result',           filterKey: 'results' },
];
const TABLE_MIN_WIDTH = 1480;

const cellBox = (width: number) => ({ flexGrow: width, flexShrink: 0, flexBasis: width, minWidth: width });

const PAGE_SIZES = [25, 50, 100] as const;
const DEFAULT_SORT_KEY = 'activityDate';

// ─── Label / color helpers ────────────────────────────────────────────────────
function formatEnumLabel(raw: string | null | undefined): string {
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Tag palette copied from the mockup's --nav-* tokens.
function pillColorsFor(label: string): { bg: string; fg: string } {
  const l = label.toLowerCase();
  if (l.includes('no improvement')) return { bg: '#FBE6E9', fg: '#A10D26' };
  if (l.includes('shows improvement')) return { bg: '#CAF3C6', fg: '#0C420B' };
  if (l.includes('complet')) return { bg: '#CAF3C6', fg: '#0C420B' };
  if (l.includes('progress') || l.includes('open')) return { bg: '#DAEEFA', fg: '#034895' };
  return { bg: '#E6E6E6', fg: '#585858' };
}

function actionTypeColorsFor(label: string): { bg: string; fg: string } {
  if (label.toLowerCase().includes('process')) return { bg: '#FCECDE', fg: '#4D2E0C' };
  return { bg: '#DAEEFA', fg: '#034895' };
}

function sourceColorsFor(label: string): { bg: string; fg: string } {
  if (label.toLowerCase().includes('pulse')) return { bg: '#0078A7', fg: '#FFFFFF' };
  return { bg: '#005CBC', fg: '#FFFFFF' };
}

function Pill({ label, variant = 'status' }: { label: string; variant?: 'status' | 'actionType' }) {
  const { bg, fg } = variant === 'actionType' ? actionTypeColorsFor(label) : pillColorsFor(label);
  return (
    <View style={[pillStyles.wrap, { backgroundColor: bg }]}>
      <Text style={[pillStyles.text, { color: fg }]} numberOfLines={1}>{label}</Text>
    </View>
  );
}
const pillStyles = StyleSheet.create({
  wrap: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, alignSelf: 'flex-start' },
  text: { fontSize: 11, lineHeight: 16, fontFamily: FontFamily.bold, fontWeight: '700' },
});

function SourcePill({ label }: { label: string }) {
  const { bg, fg } = sourceColorsFor(label);
  return (
    <View style={[pillStyles.wrap, { backgroundColor: bg }]}>
      <Text style={[pillStyles.text, { color: fg }]} numberOfLines={1}>{label}</Text>
    </View>
  );
}

// ─── Column header: 3-click sort cycle + Modal-based multi-select filter ──────
interface ColumnHeaderProps {
  col: ColDef;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (col: ColDef) => void;
  options: string[];
  selected: Set<string>;
  onFilterChange: (next: Set<string>) => void;
  openFilterKey: string | null;
  onFilterToggle: (key: string | null) => void;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  col, sortBy, sortOrder, onSort, options, selected, onFilterChange, openFilterKey, onFilterToggle,
}) => {
  const active = sortBy === col.sortKey;
  const triggerRef = useRef<View>(null);
  const [menuLayout, setMenuLayout] = useState({ top: 0, left: 0 });
  const isOpen = openFilterKey === col.key;

  const openMenu = () => {
    triggerRef.current?.measureInWindow((x, y, _w, h) => {
      setMenuLayout({ top: y + h + 4, left: x });
      onFilterToggle(col.key);
    });
  };

  return (
    <View style={[chStyles.cell, cellBox(col.width)]}>
      <TouchableOpacity style={chStyles.labelRow} onPress={() => onSort(col)} activeOpacity={0.7}>
        <Text style={[chStyles.label, active && chStyles.labelActive]}>{col.label}</Text>
        <MaterialIcons
          name={active && sortOrder === 'DESC' ? 'arrow-downward' : 'arrow-upward'}
          size={12}
          color={active ? AdminColors.blue700 : AdminColors.gray350}
        />
      </TouchableOpacity>
      {!!col.filterKey && (
        <View ref={triggerRef}>
          <TouchableOpacity onPress={openMenu} hitSlop={6} style={chStyles.filterBtn}>
            <MaterialIcons
              name="filter-list"
              size={16}
              color={selected.size > 0 ? AdminColors.blue700 : AdminColors.gray350}
            />
          </TouchableOpacity>
        </View>
      )}

      <Modal visible={isOpen} transparent animationType="none" onRequestClose={() => onFilterToggle(null)}>
        <Pressable style={StyleSheet.absoluteFill} onPress={() => onFilterToggle(null)}>
          <Pressable
            style={[chStyles.filterMenu, { top: menuLayout.top, left: menuLayout.left }]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={chStyles.filterActionsRow}>
              <TouchableOpacity onPress={() => onFilterChange(new Set(options))}>
                <Text style={chStyles.filterActionText}>Select All</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => onFilterChange(new Set())}>
                <Text style={chStyles.filterActionText}>Clear</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
              {options.length === 0 && (
                <Text style={chStyles.filterEmptyText}>No options available</Text>
              )}
              {options.map((opt) => {
                const checked = selected.has(opt);
                return (
                  <TouchableOpacity
                    key={opt}
                    style={chStyles.filterOption}
                    onPress={() => {
                      const next = new Set(selected);
                      checked ? next.delete(opt) : next.add(opt);
                      onFilterChange(next);
                    }}
                  >
                    <View style={[chStyles.checkbox, checked && chStyles.checkboxChecked]}>
                      {checked && <Text style={chStyles.checkboxTick}>✓</Text>}
                    </View>
                    <Text style={chStyles.filterOptionText} numberOfLines={1}>{formatEnumLabel(opt)}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};
const chStyles = StyleSheet.create({
  // th: padding 11px 12px, 10px bold uppercase label, 0.5px tracking, nowrap.
  cell: { flexDirection: 'row', alignItems: 'center', gap: 2, paddingHorizontal: 12, paddingVertical: 11 },
  labelRow: { flexDirection: 'row', alignItems: 'center', gap: 3, minHeight: 24 },
  label: {
    fontSize: 10, lineHeight: 14, fontWeight: '700', color: AdminColors.textSecondary, fontFamily: FontFamily.bold,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  labelActive: { color: AdminColors.blue700 },
  filterBtn: { padding: 2 },
  filterMenu: {
    position: 'absolute', backgroundColor: AdminColors.bgSurface, borderWidth: 1, borderColor: AdminColors.borderDefault,
    borderRadius: 8, width: 220, paddingVertical: 8, paddingHorizontal: 8, gap: 4,
    shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.15, shadowRadius: 16, elevation: 8,
  },
  filterActionsRow: { flexDirection: 'row', gap: 14, paddingHorizontal: 4, paddingBottom: 6 },
  filterActionText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.blue700 },
  filterOption: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, paddingHorizontal: 4 },
  filterEmptyText: { fontSize: 12, fontFamily: FontFamily.regular, color: AdminColors.textMuted, padding: 8 },
  checkbox: {
    width: 16, height: 16, borderRadius: 3, borderWidth: 1, borderColor: AdminColors.borderDefault,
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxChecked: { backgroundColor: AdminColors.blue700, borderColor: AdminColors.blue700 },
  checkboxTick: { color: '#FFFFFF', fontSize: 10 },
  filterOptionText: { fontSize: 12, fontFamily: FontFamily.regular, color: AdminColors.textPrimary, flexShrink: 1 },
});

// ─── Read-only detail overlay (row click) ─────────────────────────────────────
const DetailField: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <View style={detailStyles.field}>
    <Text style={detailStyles.fieldLabel}>{label}</Text>
    <Text style={detailStyles.fieldValue}>{value ?? '—'}</Text>
  </View>
);

const CoachingDetailModal: React.FC<{ record: CoachingHistoryRecord | null; onClose: () => void }> = ({ record, onClose }) => {
  if (!record) return null;
  return (
    <AdminModal visible={!!record} title="Coaching Item (Read Only)" onClose={onClose} width={640}>
      <ScrollView style={{ maxHeight: 520 }}>
        <View style={detailStyles.row}>
          <DetailField label="Employee" value={record.employeeName} />
          <DetailField label="Action Type" value={record.actionType} />
        </View>
        <View style={detailStyles.row}>
          <DetailField label="Focus Area" value={record.focusArea} />
          <DetailField label="Source" value={record.source} />
        </View>
        <DetailField label="Action Plan" value={record.actionPlan} />
        <DetailField label="Opportunity" value={record.opportunity} />
        <DetailField label="Employee Support" value={record.support} />
        <View style={detailStyles.row}>
          <DetailField label="Manager Submitted" value={record.managerSubmitted} />
          <DetailField label="Activity Date" value={formatLongDate(record.activityDate)} />
        </View>
        <View style={detailStyles.row}>
          <DetailField label="Status" value={formatEnumLabel(record.status)} />
          <DetailField label="Result" value={record.result ? formatEnumLabel(record.result) : '—'} />
        </View>
        {!!record.actionPlanId && (
          <View style={detailStyles.auditWrap}>
            <Text style={detailStyles.auditTitle}>Audit History</Text>
            <AuditHistory actionPlanId={record.actionPlanId} status={record.status} />
          </View>
        )}
      </ScrollView>
    </AdminModal>
  );
};
const detailStyles = StyleSheet.create({
  row: { flexDirection: 'row', gap: 16 },
  field: { flex: 1, paddingVertical: 8 },
  fieldLabel: { fontSize: 11, fontFamily: FontFamily.medium, color: AdminColors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 },
  fieldValue: { fontSize: 14, fontFamily: FontFamily.regular, color: AdminColors.textPrimary },
  auditWrap: { marginTop: 12, borderTopWidth: 1, borderTopColor: AdminColors.borderDefault, paddingTop: 12 },
  auditTitle: { fontSize: 14, fontFamily: FontFamily.bold, color: AdminColors.textPrimary, marginBottom: 8 },
});

// ─── Main component ───────────────────────────────────────────────────────────
interface PeopleHubCoachingHistoryProps {
  /** Opens the Employee Profile screen for the clicked Employee Name cell. */
  onOpenEmployee?: (employeeName: string, employeeId?: string, employeeRole?: string) => void;
  /** Scopes the table to one employee and hides the Employee Name column — used when
   *  this table is embedded inside the Employee Profile page. */
  employeeId?: string;
  employeeName?: string;
}

const PeopleHubCoachingHistory: React.FC<PeopleHubCoachingHistoryProps> = ({
  onOpenEmployee, employeeId, employeeName,
}) => {
  const isEmbedded = !!employeeId;
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const locationName: string | undefined = selectedLocation?.name ?? selectedLocation?.displayText;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_KEY);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<25 | 50 | 100>(25);
  const [activeFilters, setActiveFilters] = useState<Record<FilterParamKey, Set<string>>>({
    employeeNames: new Set(), actionTypes: new Set(), focusAreas: new Set(),
    managers: new Set(), sources: new Set(), statuses: new Set(), results: new Set(),
  });
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  const [records, setRecords] = useState<CoachingHistoryRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState<CoachingHistoryFilterOptions>({
    employeeNames: [], actionTypes: [], focusAreas: [], managers: [], sources: [], statuses: [], results: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<CoachingHistoryRecord | null>(null);

  // Debounce search input before it drives the server fetch.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const columns = useMemo(
    () => (isEmbedded ? ALL_COLUMNS.filter((c) => c.key !== 'employeeName') : ALL_COLUMNS),
    [isEmbedded],
  );

  const loadHistory = async () => {
    if (!locationName) return;
    setIsLoading(true);
    try {
      const params: CoachingHistoryParams = {
        locationName,
        search: debouncedSearch || undefined,
        sortBy, sortOrder, page, size,
        employeeNames: isEmbedded && employeeName ? [employeeName] : (activeFilters.employeeNames.size ? [...activeFilters.employeeNames] : undefined),
        actionTypes: activeFilters.actionTypes.size ? [...activeFilters.actionTypes] : undefined,
        focusAreas: activeFilters.focusAreas.size ? [...activeFilters.focusAreas] : undefined,
        managers: activeFilters.managers.size ? [...activeFilters.managers] : undefined,
        sources: activeFilters.sources.size ? [...activeFilters.sources] : undefined,
        statuses: activeFilters.statuses.size ? [...activeFilters.statuses] : undefined,
        results: activeFilters.results.size ? [...activeFilters.results] : undefined,
      };
      const res = await fetchCoachingHistory(params);
      setRecords(res.records);
      setTotal(res.total);
      setFilterOptions(res.filterOptions);
    } catch (err) {
      console.warn('Failed to load coaching history', err);
      setRecords([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationName, debouncedSearch, sortBy, sortOrder, page, size, isEmbedded, employeeName,
    activeFilters.employeeNames.size, activeFilters.actionTypes.size, activeFilters.focusAreas.size,
    activeFilters.managers.size, activeFilters.sources.size, activeFilters.statuses.size, activeFilters.results.size,
  ]);

  const handleSort = (col: ColDef) => {
    if (sortBy !== col.sortKey) {
      setSortBy(col.sortKey);
      setSortOrder('ASC');
    } else if (sortOrder === 'ASC') {
      setSortOrder('DESC');
    } else {
      setSortBy(DEFAULT_SORT_KEY);
      setSortOrder('DESC');
    }
    setPage(1);
  };

  const handleFilterChange = (key: FilterParamKey, next: Set<string>) => {
    setActiveFilters((f) => ({ ...f, [key]: next }));
    setPage(1);
  };

  const hasActiveFilters = Object.values(activeFilters).some((s) => s.size > 0);
  const clearFilters = () => {
    setActiveFilters({
      employeeNames: new Set(), actionTypes: new Set(), focusAreas: new Set(),
      managers: new Set(), sources: new Set(), statuses: new Set(), results: new Set(),
    });
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / size));

  const renderCell = (record: CoachingHistoryRecord, col: ColDef) => {
    if (col.key === 'employeeName') {
      return (
        <Pressable onPress={() => onOpenEmployee?.(record.employeeName, record.employeeId ?? undefined, record.employeeRole ?? undefined)} hitSlop={4}>
          <Text style={styles.nameCell}>{record.employeeName}</Text>
        </Pressable>
      );
    }
    if (col.key === 'actionType') return <Pill label={formatEnumLabel(record.actionType)} variant="actionType" />;
    if (col.key === 'focusArea') return <Text style={styles.focusText}>{record.focusArea || '—'}</Text>;
    if (col.key === 'source') return <SourcePill label={formatEnumLabel(record.source)} />;
    if (col.key === 'status') return <Pill label={formatEnumLabel(record.status)} />;
    if (col.key === 'result') {
      return <Text style={styles.resultText}>{record.result ? formatEnumLabel(record.result) : '—'}</Text>;
    }
    if (col.key === 'actionPlan') {
      return (
        <View>
          <Text style={styles.cellText}>{record.actionPlan || '-'}</Text>
          {!!record.support && <Text style={styles.cellSubText}>Support: {record.support}</Text>}
        </View>
      );
    }
    if (col.key === 'activityDate') {
      return <Text style={styles.cellText}>{record.activityDate ? formatLongDate(record.activityDate) : '-'}</Text>;
    }
    if (col.key === 'managerSubmitted') {
      const m = record.managerSubmitted;
      return <Text style={styles.cellText}>{m && m.includes('@') ? emailToDisplayName(m) : (m || '-')}</Text>;
    }
    const value = record[col.key];
    return <Text style={styles.cellText}>{value ? String(value) : '-'}</Text>;
  };

  return (
    <View style={styles.page}>
      {/* Title block */}
      <View style={styles.titleBlock}>
        <Text style={styles.title}>Coaching History</Text>
        <Text style={styles.subtitle}>
          Sort and filter from the table headers. Status and result remain separate so the record state never replaces the coaching outcome.
        </Text>
      </View>

      {/* Controls: search (left) + count + clear */}
      <View style={styles.toolbar}>
        <View style={styles.searchWrap}>
          <MaterialIcons name="search" size={16} color={AdminColors.gray350} />
          <TextInput
            style={styles.searchInput}
            placeholder={isEmbedded
              ? 'Search focus, plan, manager, status, or result'
              : 'Search associate, focus, plan, support, manager, status, or result'}
            placeholderTextColor={AdminColors.gray350}
            value={searchInput}
            onChangeText={setSearchInput}
          />
        </View>
        <Text style={styles.countText}>Showing {records.length} of {total} records</Text>
        {hasActiveFilters && (
          <AdminButton label="Clear Filters" variant="secondary" small onPress={clearFilters} />
        )}
        {isLoading && <ActivityIndicator size="small" color={AdminColors.blue700} />}
      </View>

      {/* Table box: 1px border, 10px radius, scrolls horizontally past 1480px (mockup .people-table-wrap) */}
      <View style={styles.tableWrap}>
        <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.tableScrollContent}>
          <View style={styles.table}>
            <View style={styles.theadRow}>
              {columns.map((col) => (
                <ColumnHeader
                  key={col.key}
                  col={col}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  options={col.filterKey ? filterOptions[col.filterKey] : []}
                  selected={col.filterKey ? activeFilters[col.filterKey] : new Set()}
                  onFilterChange={(next) => col.filterKey && handleFilterChange(col.filterKey, next)}
                  openFilterKey={openFilterKey}
                  onFilterToggle={setOpenFilterKey}
                />
              ))}
            </View>

            {!isLoading && records.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={styles.emptyText}>No coaching records match the selected filters.</Text>
              </View>
            )}

            {records.map((record, idx) => (
              <Pressable
                key={record.id}
                style={({ hovered, pressed }: any) => [
                  styles.row,
                  idx === records.length - 1 && styles.rowLast,
                  (hovered || pressed) && styles.rowHover,
                ]}
                onPress={() => setSelectedRecord(record)}
              >
                {columns.map((col) => (
                  <View key={col.key} style={[styles.cell, cellBox(col.width)]}>
                    {renderCell(record, col)}
                  </View>
                ))}
              </Pressable>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Paging — matches OnePagerTable's pagination bar */}
      <View style={styles.pagingRow}>
        <Text style={styles.paginationInfo}>
          {total === 0
            ? 'Showing 0 of 0'
            : `Showing ${Math.min((page - 1) * size + 1, total)}–${Math.min(page * size, total)} of ${total}`}
        </Text>
        <View style={styles.paginationControls}>
          <TouchableOpacity
            style={[styles.paginationButton, page <= 1 && styles.paginationButtonDisabled]}
            disabled={page <= 1}
            onPress={() => setPage((p) => Math.max(1, p - 1))}
            activeOpacity={0.7}
            accessibilityLabel="Previous page"
          >
            <MaterialIcons name="chevron-left" size={20} color={page <= 1 ? '#CED4DA' : '#005CBC'} />
          </TouchableOpacity>
          <Text style={styles.paginationPageText}>{`Page ${page} of ${totalPages}`}</Text>
          <TouchableOpacity
            style={[styles.paginationButton, page >= totalPages && styles.paginationButtonDisabled]}
            disabled={page >= totalPages}
            onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
            activeOpacity={0.7}
            accessibilityLabel="Next page"
          >
            <MaterialIcons name="chevron-right" size={20} color={page >= totalPages ? '#CED4DA' : '#005CBC'} />
          </TouchableOpacity>
        </View>
        <View style={styles.paginationSizeSelector}>
          {PAGE_SIZES.map((opt) => (
            <TouchableOpacity
              key={opt}
              style={[styles.paginationSizeOption, size === opt && styles.paginationSizeOptionActive]}
              onPress={() => { setSize(opt); setPage(1); }}
              activeOpacity={0.7}
            >
              <Text style={[styles.paginationSizeText, size === opt && styles.paginationSizeTextActive]}>{opt}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <CoachingDetailModal record={selectedRecord} onClose={() => setSelectedRecord(null)} />
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: AdminColors.bgPage, padding: 16, gap: 16 },
  titleBlock: { gap: 3 },
  title: { fontSize: 18, lineHeight: 24, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.textPrimary },
  subtitle: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.regular, color: AdminColors.textSecondary },
  toolbar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' },
  searchWrap: {
    flexGrow: 1, flexBasis: 360, maxWidth: 620, marginRight: 'auto', flexDirection: 'row', alignItems: 'center', gap: 8,
    borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6,
    paddingHorizontal: 10, height: 36, backgroundColor: AdminColors.bgSurface,
  },
  searchInput: { flex: 1, fontSize: 14, color: AdminColors.textPrimary, fontFamily: FontFamily.regular, height: 34, padding: 0 },
  countText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.blue800 },

  // .people-table-wrap: 1px border, 10px radius, white surface, scrolls horizontally.
  tableWrap: {
    width: '100%', backgroundColor: AdminColors.bgSurface,
    borderWidth: 1, borderColor: AdminColors.gray25, borderRadius: 10, overflow: 'hidden',
  },
  tableScrollContent: { minWidth: '100%' },
  table: { minWidth: TABLE_MIN_WIDTH, width: '100%' },
  // thead tr: inset 2px bottom rule in --nav-border-table-header.
  theadRow: {
    flexDirection: 'row', alignItems: 'center',
    borderBottomWidth: 2, borderBottomColor: AdminColors.borderTable, backgroundColor: AdminColors.bgSurface,
  },

  row: {
    flexDirection: 'row', alignItems: 'center',
    borderBottomWidth: 1, borderBottomColor: AdminColors.gray25,
  },
  rowLast: { borderBottomWidth: 0 },
  rowHover: { backgroundColor: AdminColors.bgHover },
  cell: { paddingHorizontal: 12, paddingVertical: 11, justifyContent: 'center' },
  cellText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.regular, color: AdminColors.textPrimary },
  cellSubText: { fontSize: 12, lineHeight: 18, fontFamily: FontFamily.regular, color: AdminColors.textSecondary, marginTop: 2 },
  focusText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.textPrimary },
  resultText: { fontSize: 12, lineHeight: 18, fontFamily: FontFamily.regular, color: AdminColors.textSecondary },
  nameWrap: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  nameCell: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.bold, fontWeight: '700', color: AdminColors.textLink },

  emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: 28 },
  emptyText: { fontSize: 14, lineHeight: 20, fontFamily: FontFamily.regular, color: AdminColors.textSecondary },

  pagingRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
  },
  // Mirrors OnePagerTable's pagination bar styling exactly.
  paginationInfo: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textSecondary },
  paginationControls: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  paginationButton: {
    width: 28, height: 28, borderRadius: 4, borderWidth: 1, borderColor: '#DEE2E6',
    alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF',
  },
  paginationButtonDisabled: { backgroundColor: '#F8F9FA', borderColor: '#F0F0F0' },
  paginationPageText: { fontFamily: FontFamily.regular, fontSize: 12, color: '#2D2D2D', minWidth: 90, textAlign: 'center' },
  paginationSizeSelector: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  paginationSizeOption: {
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, borderWidth: 1, borderColor: '#DEE2E6', backgroundColor: '#FFFFFF',
  },
  paginationSizeOptionActive: { borderColor: '#005CBC', backgroundColor: '#EBF3FF' },
  paginationSizeText: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textSecondary },
  paginationSizeTextActive: { fontFamily: FontFamily.bold, color: '#005CBC' },
});

export default PeopleHubCoachingHistory;

